import React from 'react';
import type { UserDraft } from '../CreateUpdateUserModal/component';
import { Title, Loader, ReloadIconButton, ClearFilters, TablePaginationWrapper } from '../../elements';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import {
  Grid,
  Paper,
  TableRow,
  TableBody,
  TableCell,
  Table,
  TableHead,
  Tooltip,
  IconButton,
  Icon,
  Button,
  Typography,
  TextField,
  Chip,
  Box
} from '@material-ui/core';
import { useStyles } from './styles';
import { ApiService } from '../../hooks';
import { extractApiErrorMessage, notifications } from '../../utils';
import { CreateUpdateUserModal, CreateUpdateFarmModal } from '../../components';
import { useTranslation } from 'react-i18next';
import { FarmType } from '../../models/farm';
import {
  HealthSystemDomainUsersResultUserViewModel,
  HealthSystemDomainFarmsResultFarmViewModel
} from '../../api/HealthSystemApi';
import { getTemperatureUnitLabel } from '../../utils/temperatureUnitHelper';

// Invite mode for CreateUpdateUserModal: no id, nothing filled in yet.
const EMPTY_FARM_USER: UserDraft = {};

const FarmsPage: React.FC<any> = () => {
  const classes = useStyles();
  const apiService = new ApiService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [createUserDialogOpen, setCreateUserDialogOpen] = React.useState(false);
  const [createUpdateFarmDialogOpen, setCreateUpdateFarmDialogOpen] = React.useState(false);
  const [farmMode, setFarmMode] = React.useState<'create' | 'update'>('create');
  const [selectedFarm, setSelectedFarm] = React.useState<HealthSystemDomainFarmsResultFarmViewModel | null>(null);

  const [farms, setFarms] = React.useState<HealthSystemDomainFarmsResultFarmViewModel[]>([]);
  const [farmUserData, setFarmUserData] = React.useState<UserDraft>(EMPTY_FARM_USER);
  const [farmUserFarmName, setFarmUserFarmName] = React.useState('');

  const [searchText, setSearchText] = React.useState('');

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadFarms = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.farms
      .farmsGetFarms({ Page: searchPage, Size: rowsPerPage, SearchText: searchText })
      .then((result) => {
        setFarms(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setFarms([]);
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      const loadData = async () => loadFarms();
      loadData();
    }
  }, [loaded]);

  const handleCreateFarmOrUser = (result: boolean | HealthSystemDomainUsersResultUserViewModel | null) => {
    setCreateUserDialogOpen(false);
    setCreateUpdateFarmDialogOpen(false);

    if (!result) {
      return;
    }

    if (result) {
      setLoaded(false);
    }
  };

  const openCreateFarmUserModal = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
    setFarmUserData({ farmId: farm.id });
    setFarmUserFarmName(farm.name || farm.number || '');
    setCreateUserDialogOpen(true);
  };

  const handleUpdateFarmClick = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
    setFarmMode('update');
    setSelectedFarm({ ...farm });
    setCreateUpdateFarmDialogOpen(true);
  };

  const handleCreateFarmClick = () => {
    setFarmMode('create');
    setSelectedFarm(null);
    setCreateUpdateFarmDialogOpen(true);
  };

  const renderActionCell = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
    return (
      <>
        <Tooltip arrow title={t('f-farm-edit')}>
          <IconButton id={`upd-${farm.id}`} size="small" color="primary" onClick={() => handleUpdateFarmClick(farm)}>
            <Icon>{'edit'}</Icon>
          </IconButton>
        </Tooltip>

        <Tooltip arrow title={t('f-farm-add-user')}>
          <IconButton
            id={`user-add-${farm.id}`}
            size="small"
            color="primary"
            onClick={() => openCreateFarmUserModal(farm)}
          >
            <Icon>{'person_add'}</Icon>
          </IconButton>
        </Tooltip>

        {/* <Tooltip arrow title={t('f-farm-disable')} > */}
        <IconButton id={`dis-set-${farm.id}`} size="small" disabled color="primary" onClick={() => alert(farm)}>
          <Icon>{'work_off'}</Icon>
        </IconButton>
        {/* </Tooltip> */}
      </>
    );
  };

  const renderGeoCell = (farm: HealthSystemDomainFarmsResultFarmViewModel) => {
    const { latitude, longitude } = farm;

    if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
      return null;
    }

    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

    return (
      <Box display="flex" alignItems="center">
        <Tooltip arrow title={t('f-farm-view-map')}>
          <IconButton
            component="a"
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            color="primary"
          >
            <Icon>location_on</Icon>
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const clearFilters = () => {
    setSearchText('');
  };

  const renderFiltersSection = () => {
    return (
      <Grid container spacing={1} className={classes.centred}>
        <Grid item>
          <TextField
            label={t('f-com-search-farm')}
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setLoaded(false);
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Backspace') {
                setLoaded(false);
              }
            }}
          />
        </Grid>

        <Grid item>
          <ClearFilters onClick={clearFilters} />
        </Grid>

        <Grid item>
          <Button
            id="searchBtn"
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setLoaded(false)}
            startIcon={<Icon>{'search'}</Icon>}
          >
            {t('f-search')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          {/* title & spinner */}

          <Grid container direction="row" spacing={1} className={classes.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'agriculture'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-farms')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container className={classes.justifyRight} md={6} xs={10}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <Button
                  id="createFarmBtn"
                  className={classes.withMarginLeft}
                  color="default"
                  size="small"
                  variant="contained"
                  startIcon={<Icon>{'add_circle_outline'}</Icon>}
                  onClick={handleCreateFarmClick}
                >
                  {t('f-farm-add')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {renderFiltersSection()}

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid item md={12} xs={12}>
              <TablePaginationWrapper
                rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              >
                <Paper elevation={1}>
                  <Table size="small" id="farmsResultTable">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>{'Id'}</TableCell>
                        <TableCell>{t('f-com-name')}</TableCell>
                        <TableCell>{t('f-farm-comp-number')}</TableCell>
                        <TableCell>{t('f-com-address')}</TableCell>
                        <TableCell>{t('f-geo')}</TableCell>
                        <TableCell>{t('f-temperature-unit')}</TableCell>
                        <TableCell>{t('f-farm-type')}</TableCell>
                        <TableCell>{''}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {Array.isArray(farms) &&
                        farms.map((farm) => (
                          <TableRow key={farm.id} hover>
                            <TableCell>
                              <Typography variant="caption">{farm.id}</Typography>
                            </TableCell>
                            <TableCell className={`${farm.isSoftDeleted ? classes.farmNameDeleted : classes.farmName}`}>
                              {farm.name}
                            </TableCell>
                            <TableCell>{farm.number}</TableCell>
                            <TableCell>{farm.address}</TableCell>
                            <TableCell>{renderGeoCell(farm)}</TableCell>
                            <TableCell>{getTemperatureUnitLabel(farm.metadata?.temperatureUnit)}</TableCell>
                            <TableCell>{t(FarmType[farm.type])}</TableCell>
                            <TableCell>{renderActionCell(farm)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Paper>
              </TablePaginationWrapper>
            </Grid>
          )}
        </Grid>
      </Grid>

      <CreateUpdateUserModal
        key={createUserDialogOpen ? 'add-new' : farmUserData.id}
        opened={createUserDialogOpen}
        returnObject={false}
        onModalAction={handleCreateFarmOrUser}
        user={farmUserData}
        farmName={farmUserFarmName}
      />

      <CreateUpdateFarmModal
        opened={createUpdateFarmDialogOpen}
        onModalAction={handleCreateFarmOrUser}
        mode={farmMode}
        farm={selectedFarm}
      />
    </>
  );
};

export default FarmsPage;
